import type { CompilationResult } from "@datapolarna/formulaparser";
import type { ShapeStyle } from "shared/mapStyles/domain/shapeStyle";
import type { VehicleStyle } from "shared/mapStyles/domain/vehicleStyle";
import type { MapStyle } from "shared/mapStyles/domain/mapStyle";
import { language as shapeLanguage } from 'shared/mapStyles/domain/shapeFormulaLanguage';
import { language as vehicleLanguage } from 'shared/mapStyles/domain/vehicleFormulaLanguage';
import type { MapStyleResponse } from "../api/mapStyleResponse";

export interface CreateMapStyleOptions {
  style: MapStyleResponse;
}

export interface CreateMapStyleResult {
  mapStyle: MapStyle;
  compilation: CreateMapStyleCompilationResult;
}

export interface CreateMapStyleCompilationResult {
  shapeColor?: CompilationResult;
  shapeLabel?: CompilationResult;
  shapeOutlineColor?: CompilationResult;
  shapeOutlineSize?: CompilationResult;
  vehicleColor?: CompilationResult;
  shapeFontSize?: CompilationResult;
  shapeMessageIcon?: CompilationResult;
}

export function createMapStyle(options: CreateMapStyleOptions) : CreateMapStyleResult {

  const {
    style,
  } = options;

  const {
    shapeColorFormula,
    shapeLabelFormula,
    shapeOutlineColorFormula,
    shapeOutlineSizeFormula,
    vehicleColorFormula,
    shapeFontSizeFormula,
    shapeMessageIconFormula
  } = style;

  const compilation: CreateMapStyleCompilationResult = {};


  if (shapeColorFormula) {
    compilation.shapeColor = shapeLanguage.formulaParser.compile(shapeColorFormula);
  }

  if (shapeLabelFormula) {
    compilation.shapeLabel = shapeLanguage.formulaParser.compile(shapeLabelFormula);
  }

  if (shapeOutlineColorFormula) {
    compilation.shapeOutlineColor = shapeLanguage.formulaParser.compile(shapeOutlineColorFormula);
  }

  if (shapeOutlineSizeFormula) {
    compilation.shapeOutlineSize = shapeLanguage.formulaParser.compile(shapeOutlineSizeFormula);
  }

  if (vehicleColorFormula) {
    compilation.vehicleColor = vehicleLanguage.formulaParser.compile(vehicleColorFormula);
  }

  if (shapeFontSizeFormula) {
    compilation.shapeFontSize = shapeLanguage.formulaParser.compile(shapeFontSizeFormula);
  }

  if (shapeMessageIconFormula) {
    compilation.shapeMessageIcon = shapeLanguage.formulaParser.compile(shapeMessageIconFormula);
  }

  const shapeStyle: ShapeStyle = {
    color(context) {
      if (shapeColorFormula) {
        const result = shapeLanguage.formulaParser.evaluate(shapeColorFormula, context);
        if (!result.compilation.success) {
          return '#ffffff';
        }
        return result.text.length > 0 ? result.text : '#ffffff';
      }
      return '#ffffff';
    },
    label(context) {
      if (shapeLabelFormula) {
        const result = shapeLanguage.formulaParser.evaluate(shapeLabelFormula, context);
        if (!result.compilation.success) {
          return '';
        }
        return result.text;
      }
      return '';
    },
    outlineColor(context) {
      if (shapeOutlineColorFormula) {
        const result = shapeLanguage.formulaParser.evaluate(shapeOutlineColorFormula, context);
        if (!result.compilation.success) {
          return '#000000';
        }
        return result.text;
      }
      return '#000000';
    },
    outlineSize(context) {
      if (shapeOutlineSizeFormula) {
        const result = shapeLanguage.formulaParser.evaluate(shapeOutlineSizeFormula, context);
        if (!result.compilation.success) {
          return 1;
        }

        const size = parseFloat(result.text);
        return size;
      }
      return 1;
    },
    fontSize(context) {
      if (shapeFontSizeFormula) {
        const result = shapeLanguage.formulaParser.evaluate(shapeFontSizeFormula, context);
        if (!result.compilation.success) {
          return 12;
        }

        const size = parseFloat(result.text);
        return size;
      }
      return 12;
    },
    messageIconVisible(context) {

      if (shapeMessageIconFormula) {
        const result = shapeLanguage.formulaParser.evaluate(shapeMessageIconFormula, context);
        if (result.compilation.success) {
          if (result.text && result.text !== 'false' && result.text !== '0') {
            return true;
          } else {
            return false;
          }
        }
      }

      const note = context.storageArea?.note;
      return note !== undefined && note !== '';
    },
  };

  const vehicleStyle: VehicleStyle = {
    color(context) {
      if (vehicleColorFormula) {
        const result = vehicleLanguage.formulaParser.evaluate(vehicleColorFormula, context);
        if (!result.compilation.success) {
          return '#000000';
        }
        return result.text;
      }

      return context.color ?? '#000000';
    },
  };

  const mapStyle: MapStyle = {
    id: style.id,
    name: style.name,
    shape: shapeStyle,
    vehicle: vehicleStyle
  };

  return {
    mapStyle,
    compilation
  };
}
